// The issue log on the customer profile, Section 7.7: problems in the
// relationship, each with a severity and a date. Internal team material; the
// profile skips the card for the franchisee role and the server refuses it
// regardless.

import { useCallback, useEffect, useState } from 'react'
import { listIssues, addIssue, resolveIssue, removeIssue } from '../api.js'
import {
  Button,
  DateField,
  EmptyState,
  Input,
  Notice,
  Panel,
  Pill,
  Select,
} from '../components/ui/index.js'
import { severityTone } from '../tones/issues.js'
import { useConfirm } from '../confirm.jsx'
import { useApiForm } from '../useApiForm.js'
import { ISSUE_SEVERITIES, severityLabel, formatDate, todayDateOnly } from '../vocab.js'

function IssueRow({ customerId, issue, canWrite, onChanged }) {
  const confirm = useConfirm()
  const [resolving, setResolving] = useState(false)
  const [note, setNote] = useState('')
  const [error, setError] = useState(null)

  async function doResolve() {
    setError(null)
    try {
      await resolveIssue(customerId, issue.id, { resolutionNote: note || null })
      setResolving(false)
      setNote('')
      onChanged()
    } catch (caught) {
      setError(caught.message)
    }
  }

  async function drop() {
    if (!(await confirm('Remove this issue from the log?'))) return
    setError(null)
    try {
      await removeIssue(customerId, issue.id)
      onChanged()
    } catch (caught) {
      setError(caught.message)
    }
  }

  const resolved = issue.status === 'RESOLVED'

  return (
    <div className="row">
      <div className="row-main">
        <div className={`row-primary wraps${resolved ? ' muted' : ''}`}>{issue.description}</div>
        <div className="row-meta">
          {resolved
            ? `Occurred ${formatDate(issue.occurredOn)}, resolved ${formatDate(issue.resolvedOn)}${
                issue.resolutionNote ? `: ${issue.resolutionNote}` : ''
              }`
            : `Occurred ${formatDate(issue.occurredOn)}${
                issue.enteredByName ? `, logged by ${issue.enteredByName}` : ''
              }`}
        </div>
        {error ? <div className="row-error">{error}</div> : null}
        {resolving ? (
          <div className="btnrow">
            <Input
              className="grow"
              type="text"
              placeholder="How it was resolved"
              value={note}
              aria-label="Resolution note"
              onChange={(event) => setNote(event.target.value)}
            />
            <Button variant="primary" onClick={doResolve}>
              Mark resolved
            </Button>
            <Button variant="ghost" onClick={() => setResolving(false)}>
              Keep open
            </Button>
          </div>
        ) : null}
      </div>
      <div className="row-trail">
        <Pill tone={resolved ? 'neutral' : severityTone[issue.severity]}>
          {resolved ? 'Resolved' : severityLabel(issue.severity)}
        </Pill>
        {canWrite && !resolved && !resolving ? (
          <Button variant="ghost" onClick={() => setResolving(true)}>
            Resolve
          </Button>
        ) : null}
        {canWrite ? (
          <Button variant="ghost" onClick={drop}>
            Remove
          </Button>
        ) : null}
      </div>
    </div>
  )
}

export function IssuesCard({ customerId, canWrite }) {
  const [issues, setIssues] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({
    description: '',
    severity: 'MEDIUM',
    occurredOn: todayDateOnly(),
  })
  const { saving, error, fieldErrors, submit } = useApiForm()

  const load = useCallback(async () => {
    try {
      const data = await listIssues(customerId)
      setIssues(data.issues)
      setLoadError(null)
    } catch (caught) {
      setIssues([])
      setLoadError(caught.message)
    }
  }, [customerId])

  useEffect(() => {
    load()
  }, [load])

  async function onSubmit(event) {
    event.preventDefault()
    const result = await submit(() => addIssue(customerId, form))
    if (result) {
      setForm({ description: '', severity: 'MEDIUM', occurredOn: todayDateOnly() })
      setAdding(false)
      load()
    }
  }

  const open = issues?.filter((issue) => issue.status === 'OPEN') ?? []

  return (
    <Panel
      title="Issues"
      support={issues === null ? 'Loading' : open.length ? `${open.length} open` : 'Nothing open'}
    >
      {loadError ? <Notice tone="bad">{loadError}</Notice> : null}
      {issues && issues.length ? (
        <div className="rows">
          {issues.map((issue) => (
            <IssueRow
              key={issue.id}
              customerId={customerId}
              issue={issue}
              canWrite={canWrite}
              onChanged={load}
            />
          ))}
        </div>
      ) : issues && !loadError ? (
        <EmptyState title="No issues logged">
          Problems in the relationship go here with a severity and a date, so the record is there
          when the account comes up.
        </EmptyState>
      ) : null}

      {canWrite && adding ? (
        <form className="card-form" onSubmit={onSubmit} noValidate>
          {error ? <Notice tone="bad">{error}</Notice> : null}
          <div className="formgrid">
            <Input
              id="issue-description"
              className="span-2"
              label="What happened"
              multiline
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({ ...current, description: event.target.value }))
              }
              error={fieldErrors.description}
            />
            <Select
              id="issue-severity"
              label="Severity"
              value={form.severity}
              onChange={(severity) => setForm((current) => ({ ...current, severity }))}
              options={ISSUE_SEVERITIES.map((entry) => ({
                value: entry.value,
                label: entry.label,
              }))}
            />
            <DateField
              id="issue-occurred"
              label="When it occurred"
              value={form.occurredOn}
              onChange={(occurredOn) => setForm((current) => ({ ...current, occurredOn }))}
            />
          </div>
          <div className="btnrow">
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <span className="spacer" />
            <Button type="submit" variant="primary" disabled={saving || !form.description.trim()}>
              {saving ? 'Saving' : 'Log issue'}
            </Button>
          </div>
        </form>
      ) : null}

      {canWrite && !adding ? (
        <div className="btnrow">
          <span className="spacer" />
          <Button onClick={() => setAdding(true)}>Log an issue</Button>
        </div>
      ) : null}
    </Panel>
  )
}
